/**
 * Calculator Constants for Krinos HVAC Assessment Platform
 *
 * This file contains all configuration constants, lookup tables,
 * and regional data used in HVAC assessment calculations.
 *
 * Regional Focus: Triad NC (Climate Zone 4A)
 */

/* =========================
   Regional + Pricing Config
   ========================= */
export const REGIONAL_CONFIG = {
  averageSystemLife: 10,
  concernAge: 8,
  typicalLifespan: '10–12 years'
};

/* =========================
   Climate & Conversion Constants
   ========================= */
export const CLIMATE_ZONE_4A = {
  coolingHours: 1400,  // Triad NC specific
  heatingHours: 2200   // Triad NC specific
};

/**
 * Phase 5: Gas furnace AFUE ratings by age
 */
export const AFUE_BY_AGE = {
  0: { standard: 80, highEfficiency: 96 },
  5: { standard: 80, highEfficiency: 95 },
  10: { standard: 78, highEfficiency: 92 },
  15: { standard: 75, highEfficiency: 90 },
  20: { standard: 70, highEfficiency: 85 },
  25: { standard: 65, highEfficiency: 80 }
};

export const GAS_PRICE_NC = 1.20; // $/therm (NC average 2025)

export const SEER2_CONVERSION = {
  split: 0.95,     // SEER2 = SEER * 0.95 for split systems
  packaged: 0.96   // SEER2 = SEER * 0.96 for packaged units
};

export const HSPF2_CONVERSION = 0.85; // HSPF2 = HSPF * 0.85

/**
 * Age-based SEER estimation lookup table
 */
export const SEER_BY_INSTALLATION_YEAR = {
  2023: { min: 15, typical: 16, note: "SEER2 era begins" },
  2015: { min: 14, typical: 16 },
  2006: { min: 13, typical: 14 },
  1998: { min: 10, typical: 12 },
  1992: { min: 10, typical: 11 },
  1980: { min: 8, typical: 9 },
  1970: { min: 6, typical: 7 }
};

/**
 * Efficiency degradation parameters
 */
export const EFFICIENCY_DEGRADATION = {
  annualRate: 0.01,     // 1% per year
  maxDegradation: 0.30  // Cap at 30% total degradation
};

/**
 * Home envelope impact factors (Phase 3)
 */
export const ENVELOPE_FACTORS = {
  airTightness: {
    very_drafty: { multiplier: 1.35, ach50: 10 },
    somewhat_drafty: { multiplier: 1.20, ach50: 7 },
    average: { multiplier: 1.10, ach50: 5 },
    well_sealed: { multiplier: 1.00, ach50: 3 }
  },
  insulation: {
    poor: { multiplier: 1.30 },
    fair: { multiplier: 1.15 },
    average: { multiplier: 1.00 },
    good: { multiplier: 0.85 }
  },
  windows: {
    single: { multiplier: 1.25, uValue: 1.0 },
    double: { multiplier: 1.00, uValue: 0.5 },
    double_lowE: { multiplier: 0.90, uValue: 0.35 },
    triple: { multiplier: 0.80, uValue: 0.2 }
  }
};

/**
 * Duct efficiency factors (Phase 2)
 */
export const DUCT_EFFICIENCY = {
  poor: 0.72,     // 28% loss
  fair: 0.82,     // 18% loss
  good: 0.90,     // 10% loss
  excellent: 0.95, // 5% loss
  ductless: 1.00  // No loss
};

/**
 * Duke Energy Smart $aver® 2025 (NC) – simplified matrix for educational estimates
 *
 * CCF = Complete Compressor Failure (standard rebate)
 * EARLY = Early Retirement (higher rebate for working systems)
 */
export const DUKE_2025 = {
  ac: [
    { minSeer2: 15.2, rebate: { CCF: 200, EARLY: 300 }, requires: ["ductSealing","ecm"] },
    { minSeer2: 16.0, rebate: { CCF: 300, EARLY: 400 }, requires: ["ductSealing","ecm"] },
    { minSeer2: 17.0, rebate: { CCF: 400, EARLY: 500 }, requires: ["ductSealing","ecm"] }
  ],
  heatPump: [
    { minSeer2: 15.2, minHspf2: 7.5, rebate: { CCF: 350, EARLY: 700 }, requires: ["ductSealing","ecm"] },
    { minSeer2: 16.0, minHspf2: 7.5, rebate: { CCF: 450, EARLY: 800 }, requires: ["ductSealing","ecm"] },
    { minSeer2: 17.0, minHspf2: 7.5, rebate: { CCF: 700, EARLY: 900 }, requires: ["ductSealing","ecm"] }
  ],
  miniSplit: [
    { minSeer2: 18.0, minHspf2: 9.0, rebate: { CCF: 700, EARLY: 900 }, requires: ["ecm"] }
  ],
  conversionElectricToHP: { minSeer2: 15.2, minHspf2: 7.5, rebate: 1500 },
  smartThermostatWithHVAC: 125,
  standAlone: {
    ductSealing: 350,            // ≥12% leakage reduction
    atticInsulationAirSeal: 700, // R-30+ plus ≥5% leakage reduction, ≥1,000 ft²
    hpWaterHeater: 500,
    vsPoolPump: 900
  }
};

/**
 * Age-based repair threshold curve (region-specific)
 * Used to determine repair vs replace recommendation
 */
export const AGE_THRESHOLDS = {
  0: 2000, 1: 1900, 2: 1800, 3: 1700, 4: 1600,
  5: 1500, 6: 1400, 7: 1200, 8: 1000, 9: 800,
  10: 600, 11: 500, 12: 400, 13: 350, 14: 300,
  15: 250, 16: 200, 17: 200, 18: 200, 19: 200,
  20: 200, 21: 200, 22: 200, 23: 200, 24: 200, 25: 200
};

/**
 * Energy cost estimation parameters (Triad NC, Climate Zone 4A)
 */
export const ENERGY_COST_PARAMS = {
  costPerSqFt: {
    centralAC: { cooling: 0.225, heating: 0 },
    heatPump: { cooling: 0.225, heating: 0.325 },
    furnaceAC: { cooling: 0.225, heating: 0.275 },
    ductlessMini: { cooling: 0.190, heating: 0.260 }
  },
  sizeScaling: {
    small: { maxSqft: 1000, multiplier: 1.15 },
    mediumSmall: { maxSqft: 1500, multiplier: 1.05 },
    mediumLarge: { minSqft: 2500, maxSqft: 3000, multiplier: 0.95 },
    large: { minSqft: 3000, multiplier: 0.92 }
  },
  ductConditionMultipliers: {
    poor: 1.28,
    fair: 1.12,
    good: 1.05,
    excellent: 1.00
  },
  electricRate: 0.12, // $/kWh (NC average)
  minAnnualCost: 300,
  baseSeerForCalculation: 14 // Normalization baseline
};

/**
 * Maintenance projection parameters
 * Based on 2024-2025 industry data from Angi, HomeGuide, This Old House, EcoWatch
 */
export const MAINTENANCE_PARAMS = {
  annualCostByAge: {
    0: 150,   // New: just annual maintenance
    3: 200,   // Young: maintenance + rare minor repairs
    6: 300,   // Middle age: maintenance + occasional repairs
    9: 450,   // Approaching 10 years: more frequent issues
    11: 600,  // Past 10 years: regular repairs expected
    13: 800,  // 15+ years: frequent repairs, components failing
    16: 1000  // Very old: constant repairs needed
  },
  refrigerantMultiplier: {
    r454b: 1.0,   // New standard (2025+)
    r410a: 1.0,   // Current standard
    r22: 1.5,     // R-22 repairs cost significantly more
    unsure: 1.1
  },
  ductConditionMultiplier: {
    poor: 1.1,
    fair: 1.05,
    good: 1.02,
    excellent: 1.00
  },
  usageMultiplier: {
    small: { maxSqft: 1500, multiplier: 0.9 },  // light usage
    large: { minSqft: 3000, multiplier: 1.1 }   // heavy usage
  },
  majorRepairProbability: {
    0: 0.05,   // 0-5 years: rare (usually under warranty)
    6: 0.15,   // 6-10 years: occasional
    11: 0.40,  // 11-15 years: common (systems "show their age")
    16: 0.60   // 16+ years: very likely
  },
  avgMajorRepair: 800, // Between typical $350-600 repairs and $2000+ major components
  newSystemAnnual: 150
};

/**
 * Scoring parameters for assessment
 */
export const SCORING_PARAMS = {
  youngSystemPenalty: {
    veryYoung: { maxAge: 5, penalty: 30 },
    young: { maxAge: 7, penalty: 15 }
  },
  agePoints: {
    veryYoung: { maxAge: 5, points: 0 },
    young: { maxAge: 7, points: 10 },
    middleAge: { maxAge: 9, points: 20 },
    atLifespan: { maxAge: 12, points: 25 },
    pastLifespan: { points: 30 }
  },
  repairHistoryPoints: {
    never: 0,
    '2+years': 5,
    '1-2years': 10,
    thisYear: 15
  },
  refrigerantPoints: {
    r454b: 0,
    r410a: 5,
    r22: 20,
    unsure: 0
  },
  ductConditionPoints: {
    poor: 12,
    fair: 6,
    good: 2,
    excellent: 0
  },
  comfortIssueBasePoints: 3,
  comfortMultiplierByAge: {
    young: { maxAge: 5, multiplier: 0.3 },
    middleAge: { maxAge: 10, multiplier: 0.7 },
    old: { multiplier: 1.0 }
  }
};

/**
 * Recommendation thresholds
 */
export const RECOMMENDATION_THRESHOLDS = {
  youngSystem: {
    veryYoung: {
      maxAge: 5,
      thresholdMultiplier: 2.5,
      repairThreshold: 0.9
    },
    young: {
      maxAge: 8,
      thresholdMultiplier: 1.5,
      repairThreshold: 0.8,
      considerThreshold: 1.1
    }
  },
  standard: {
    repairThreshold: 0.8,
    considerThreshold: 1.2
  },
  scoreThresholds: {
    considerScore: 50,
    replaceScore: 60
  }
};

/**
 * New system efficiency tiers
 */
export const NEW_SYSTEM_TIERS = {
  standard: {
    name: 'Standard Efficiency',
    description: 'Meets current standards',
    seer2: {
      default: 15.2,
      ductlessMini: 17.1
    },
    afue: 80
  },
  high: {
    name: 'High Efficiency',
    description: 'ENERGY STAR® level',
    seer2: {
      default: 17.1,
      ductlessMini: 19.0
    },
    afue: 90
  },
  premium: {
    name: 'Premium Efficiency',
    description: 'Top tier savings',
    seer2: {
      default: 20.9,
      ductlessMini: 20.9
    },
    afue: 96
  }
};

/**
 * System type labels for display
 */
export const SYSTEM_TYPE_LABELS = {
  centralAC: 'Central AC',
  heatPump: 'Heat Pump',
  furnaceAC: 'Furnace + AC',
  ductlessMini: 'Ductless Mini-Split',
  furnaceOnly: 'Gas Furnace'
};

/**
 * Refrigerant type information
 */
export const REFRIGERANT_INFO = {
  r454b: 'Current EPA-approved standard with stable pricing and wide availability.',
  r410a: 'Phasing out 2025–2030. Prices likely to rise as production declines.',
  r22: 'Production ceased in 2020. Limited supply; repairs requiring R-22 can be very expensive.',
  unsure: 'Check the data plate on your outdoor unit for refrigerant type.'
};

/**
 * Comfort issue categories
 */
export const COMFORT_ISSUES = {
  hotCold: { label: 'Temperature imbalance', category: 'comfort', icon: '🌡️' },
  humidity: { label: 'Humidity control', category: 'comfort', icon: '💧' },
  runsConstantly: { label: 'Constant operation', category: 'efficiency', icon: '⏱️' },
  highBills: { label: 'High energy costs', category: 'efficiency', icon: '⚡' },
  loud: { label: 'Noise issues', category: 'quality', icon: '🔊' },
  tempTrouble: { label: 'Temperature maintenance', category: 'comfort', icon: '🎯' }
};

/**
 * Validation limits
 */
export const VALIDATION_LIMITS = {
  coolingCost: {
    min: 200,
    max: 3000  // AC-only systems
  },
  heatingCost: {
    min: 150,
    max: 2500  // Gas furnace only
  },
  heatPumpCost: {
    min: 300,
    max: 15000  // Heat pumps with both heating + cooling (much higher!)
  },
  systemAge: {
    min: 0,
    max: 30
  },
  squareFootage: {
    min: 500,
    max: 10000
  },
  repairCost: {
    min: 0,
    max: 10000
  }
};
